// renderer.ts — the frame driver. Owns the GPU, the per-frame globals, and the
// requestAnimationFrame loop. A Scene supplies the art; the renderer supplies
// the heartbeat.

import { Clock, type Frame } from "./clock";
import { GPU } from "./gpu";
import { Globals } from "./uniforms";

export interface Scene {
  /** Update uniforms / state for this frame, before anything is encoded. */
  update(frame: Frame, globals: Globals): void;
  /** Record draw calls into the swap-chain render pass. */
  draw(pass: GPURenderPassEncoder, frame: Frame): void;
}

export class Renderer {
  readonly gpu: GPU;
  readonly globals: Globals;
  private readonly clock = new Clock();
  private scene: Scene | null = null;
  private raf = 0;
  private running = false;

  private constructor(gpu: GPU) {
    this.gpu = gpu;
    this.globals = new Globals(gpu);
    this.syncResolution();
  }

  static async create(canvas: HTMLCanvasElement): Promise<Renderer> {
    const gpu = await GPU.create(canvas);
    return new Renderer(gpu);
  }

  private syncResolution(): void {
    this.globals.resolution = [this.gpu.size.width, this.gpu.size.height];
  }

  setScene(scene: Scene): void {
    this.scene = scene;
  }

  start(): void {
    if (this.running) return;
    this.running = true;
    const loop = (now: number) => {
      if (!this.running) return;
      this.frame(now);
      this.raf = requestAnimationFrame(loop);
    };
    this.raf = requestAnimationFrame(loop);
  }

  stop(): void {
    this.running = false;
    cancelAnimationFrame(this.raf);
  }

  private frame(now: number): void {
    const frame = this.clock.tick(now);

    if (this.gpu.resize()) {
      this.syncResolution();
    }

    const { globals } = this;
    this.scene?.update(frame, globals);
    globals.upload();

    const encoder = this.gpu.device.createCommandEncoder({ label: "frame" });
    const pass = encoder.beginRenderPass({
      label: "swapchain pass",
      colorAttachments: [
        {
          view: this.gpu.currentView(),
          clearValue: { r: 0.02, g: 0.024, b: 0.04, a: 1 },
          loadOp: "clear",
          storeOp: "store",
        },
      ],
    });

    if (this.scene) this.scene.draw(pass, frame);

    pass.end();
    this.gpu.device.queue.submit([encoder.finish()]);
  }
}
